import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {useBlocker} from 'react-router-dom';

import {useEventTracking} from 'src/hooks/useEventTracking';

import {Dialog} from '@pb/kit-component-dialog-v2';

import {ConfirmModal} from './confirm-modal.component';
import {ConfirmationContext} from './confirmation-context';

export const ConfirmationContextProvider = ({children}: {children: React.ReactNode}) => {
    const [changedChunks, setChangedChunks] = useState<Array<string>>([]);
    const [isShown, setIsShown] = useState<boolean>(false);
    const {unsavedChanges} = useEventTracking();

    const changedChunksRef = useRef<string[]>([]);
    changedChunksRef.current = changedChunks;

    const blocker = useBlocker(() => changedChunksRef.current.length > 0);
    const [isNavBlockerShown, setIsNavBlockerShown] = useState(false);

    useEffect(() => {
        if (blocker.state === 'blocked') {
            setIsNavBlockerShown(true);
        }
    }, [blocker.state]);

    const onConfirmCallback = useRef<() => void>(() => undefined);

    const addChangedChunk = useCallback((name: string) => {
        setChangedChunks(chunks => [...new Set([...chunks, name])]);
    }, []);

    const removeChangedChunk = useCallback((name: string) => {
        setChangedChunks(chunks => chunks.filter(chunk => chunk !== name));
    }, []);

    const onShowModal = useCallback((callback: () => void) => {
        setIsShown(true);
        onConfirmCallback.current = callback;
    }, []);

    const isChunkChanged = useCallback(
        (chunkName: string) => changedChunks.includes(chunkName),
        [changedChunks],
    );

    const value = useMemo(() => ({
        changedChunks,
        isChunkChanged,
        addChangedChunk,
        removeChangedChunk,
        onShowModal,
    }), [changedChunks, isChunkChanged, addChangedChunk, removeChangedChunk, onShowModal]);

    return (
        <ConfirmationContext.Provider value={value}>
            {children}
            <Dialog
                shown={isShown}
                setShown={nextShown => {
                    if (!nextShown) {
                        unsavedChanges({action: 'Cancel'});
                        setIsShown(false);
                    }
                }}>
                <ConfirmModal
                    onConfirm={() => {
                        unsavedChanges({action: 'Leave Page'});
                        if (isShown && onConfirmCallback.current) {
                            onConfirmCallback.current();
                            setIsShown(false);
                        }
                        setChangedChunks([]);
                    }}
                    onClose={() => {
                        setIsShown(false);
                        unsavedChanges({action: 'Cancel'});
                    }}
                />
            </Dialog>
            {isNavBlockerShown ? (
                <Dialog
                    shown={true}
                    setShown={nextShown => {
                        if (!nextShown) {
                            setIsNavBlockerShown(false);
                            blocker.reset?.();
                            unsavedChanges({action: 'Cancel'});
                        }
                    }}>
                    <ConfirmModal
                        onConfirm={() => {
                            unsavedChanges({action: 'Leave Page'});
                            changedChunksRef.current = [];
                            setIsNavBlockerShown(false);
                            blocker.proceed?.();
                            setChangedChunks([]);
                        }}
                        onClose={() => {
                            setIsNavBlockerShown(false);
                            blocker.reset?.();
                            unsavedChanges({action: 'Cancel'});
                        }}
                    />
                </Dialog>
            ) : null}
        </ConfirmationContext.Provider>
    );
};
